/**
 * セッションストア（複数アカウント対応）
 *
 * アカウント切り替え機能のための、ログイン済みアカウントの永続化を一元管理するモジュール。
 *
 * 設計方針:
 * - 「アクティブなセッションID」は従来どおり `localStorage` の `'session-id'` キーに保持する。
 *   各 StoreAdapter は `localStorage.getItem('session-id')` を直接参照して
 *   `X-Session-Id` ヘッダーに設定しているため、この互換性を維持する。
 * - ログイン済みアカウントの一覧（sessionId + 表示用メタ情報）は `'session-accounts'` キーに
 *   JSON 配列として保持する。先頭が「一番上」のアカウント。
 * - ストレージへの読み書きはすべてこのモジュールに閉じ込める。
 *   将来 Cookie 保存へ移行する際は、このファイルの storage アクセスだけを差し替えればよい。
 */

const ACTIVE_SESSION_KEY = 'session-id';
const ACCOUNTS_KEY = 'session-accounts';

/** ログイン済みアカウントの永続化情報（表示用メタ + セッションID） */
export type StoredAccount = {
  sessionId: string;
  userId: string;
  userName: string;
  mailAddress: string;
};

// ---------------------------------------------------------------------------
// 低レベルストレージアクセス（将来 Cookie 化する場合はこの2関数群のみ差し替える）
// ---------------------------------------------------------------------------

const readRaw = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeRaw = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ストレージ書き込み不可（プライベートモード等）は握りつぶす
  }
};

const removeRaw = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // no-op
  }
};

// ---------------------------------------------------------------------------
// アクティブセッション
// ---------------------------------------------------------------------------

/** 現在アクティブなセッションIDを取得する */
export const getActiveSessionId = (): string | null => readRaw(ACTIVE_SESSION_KEY);

/** アクティブなセッションIDを設定する */
export const setActiveSessionId = (sessionId: string): void => {
  writeRaw(ACTIVE_SESSION_KEY, sessionId);
};

/** アクティブなセッションIDを削除する */
export const clearActiveSessionId = (): void => {
  removeRaw(ACTIVE_SESSION_KEY);
};

// ---------------------------------------------------------------------------
// アカウント一覧
// ---------------------------------------------------------------------------

/** ログイン済みアカウント一覧を取得する（先頭が一番上） */
export const getAccounts = (): StoredAccount[] => {
  const raw = readRaw(ACCOUNTS_KEY);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (a): a is StoredAccount =>
        a &&
        typeof a.sessionId === 'string' &&
        typeof a.userId === 'string' &&
        typeof a.userName === 'string' &&
        typeof a.mailAddress === 'string',
    );
  } catch {
    return [];
  }
};

const saveAccounts = (accounts: StoredAccount[]): void => {
  writeRaw(ACCOUNTS_KEY, JSON.stringify(accounts));
};

/**
 * アカウントを一覧に追加（または既存分を更新）する。
 * 同一 userId は重複させず、追加したアカウントを先頭（一番上）に移動する。
 */
export const upsertAccount = (account: StoredAccount): void => {
  const others = getAccounts().filter((a) => a.userId !== account.userId);
  saveAccounts([account, ...others]);
};

/** 指定 sessionId のアカウントを一覧から削除する */
export const removeAccountBySessionId = (sessionId: string): void => {
  saveAccounts(getAccounts().filter((a) => a.sessionId !== sessionId));
};

/** sessionId からアカウントを取得する */
export const findAccountBySessionId = (sessionId: string): StoredAccount | undefined =>
  getAccounts().find((a) => a.sessionId === sessionId);
